import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { authFetch } from "../../utils/authFetch";
import Swal from "sweetalert2";
import "./AdminDashboard.css";

const AdminDashboard = () => {
    const [users, setUsers] = useState([]);
    const [movies, setMovies] = useState([]);
    const [searchUser, setSearchUser] = useState("");
    const [searchMovie, setSearchMovie] = useState("");

    const [userPage, setUserPage] = useState(0);
    const [userTotalPages, setUserTotalPages] = useState(0);
    const [moviePage, setMoviePage] = useState(0);
    const [movieTotalPages, setMovieTotalPages] = useState(0);

    const pageSize = 10;

    // Hàm fetch users với tìm kiếm
    const fetchUsers = useCallback(async () => {
        const baseUrl = process.env.REACT_APP_API_BASE_URL;
        try {
            let url = `${baseUrl}/users?page=${userPage}&size=${pageSize}`;
            if (searchUser) {
                url = `${baseUrl}/users/search?keyword=${encodeURIComponent(searchUser)}&page=${userPage}&size=${pageSize}`;
            }

            const res = await authFetch(url);
            const data = await res.json();
            setUsers(Array.isArray(data.content) ? data.content : []);
            setUserTotalPages(data.totalPages || 1);
        } catch (error) {
            console.error("Error fetching users:", error);
            setUsers([]);
        }
    }, [userPage, pageSize, searchUser]);

    // Hàm fetch movies với tìm kiếm
    const fetchMovies = useCallback(async () => {
        const baseUrl = process.env.REACT_APP_API_BASE_URL;
        try {
            let url = `${baseUrl}/movies?page=${moviePage}&size=${pageSize}&sort=title,asc`;
            if (searchMovie) {
                url = `${baseUrl}/movies/search?keyword=${encodeURIComponent(searchMovie)}&page=${moviePage}&size=${pageSize}`;
            }

            const res = await fetch(url);
            const data = await res.json();
            setMovies(Array.isArray(data.content) ? data.content : []);
            setMovieTotalPages(data.totalPages || 1);
        } catch (error) {
            console.error("Error fetching movies:", error);
            setMovies([]);
        }
    }, [moviePage, pageSize, searchMovie]);

    // Thêm debounce để tránh gọi API liên tục khi gõ phím
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchUsers();
        }, 500); // 0.5s delay

        return () => clearTimeout(timer);
    }, [fetchUsers, searchUser]);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchMovies();
        }, 500);

        return () => clearTimeout(timer);
    }, [fetchMovies, searchMovie]);

    // Reset trang về 0 khi search thay đổi
    useEffect(() => {
        setUserPage(0);
    }, [searchUser]);

    useEffect(() => {
        setMoviePage(0);
    }, [searchMovie]);

    const handleDelete = async (id, type) => {
        const baseUrl = process.env.REACT_APP_API_BASE_URL;
        const confirm = await Swal.fire({
            title: 'Xác nhận xoá',
            text: `Bạn có chắc chắn muốn xoá ${type === 'users' ? 'người dùng' : 'phim'} này?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Xoá',
            cancelButtonText: 'Hủy'
        });

        if (!confirm.isConfirmed) return;

        const token = localStorage.getItem("jwtToken");
        const url = `${baseUrl}/${type}/${id}`;

        try {
            const res = await fetch(url, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (!res.ok) {
                const errText = await res.text();
                throw new Error(errText || "Xoá không thành công");
            }

            Swal.fire({
                title: 'Đã xoá!',
                text: `${type === 'users' ? 'Người dùng' : 'Phim'} đã được xoá thành công.`,
                icon: 'success',
                timer: 1500,
                showConfirmButton: false
            });

            if (type === "users") {
                if (users.length === 1 && userPage > 0) {
                    setUserPage(prev => prev - 1);
                } else {
                    fetchUsers();
                }
            } else {
                if (movies.length === 1 && moviePage > 0) {
                    setMoviePage(prev => prev - 1);
                } else {
                    fetchMovies();
                }
            }
        } catch (error) {
            console.error("Lỗi khi xoá:", error);
            Swal.fire({
                title: 'Lỗi',
                text: error.message || 'Không thể xoá',
                icon: 'error'
            });
        }
    };

    const handleUserPageChange = (newPage) => {
        setUserPage(newPage);
    };

    const handleMoviePageChange = (newPage) => {
        setMoviePage(newPage);
    };

    const renderPagination = (currentPage, totalPages, onPageChange) => (
        <div className="pagination">
            <button
                onClick={() => onPageChange(Math.max(currentPage - 1, 0))}
                disabled={currentPage === 0}
            >
                ◀ Trang trước
            </button>
            <span>Trang {currentPage + 1} / {totalPages}</span>
            <button
                onClick={() => onPageChange(Math.min(currentPage + 1, totalPages - 1))}
                disabled={currentPage >= totalPages - 1 || totalPages === 0}
            >
                Trang sau ▶
            </button>
        </div>
    );

    return (
        <div className="admin-dashboard">
            <h1>Quản trị hệ thống rạp phim</h1>

            {/* USERS SECTION */}
            <div className="section">
                <div className="flex-between">
                    <h2>Quản lý người dùng</h2>
                    <input
                        type="text"
                        className="search"
                        placeholder="Tìm kiếm người dùng"
                        value={searchUser}
                        onChange={(e) => setSearchUser(e.target.value)}
                    />
                </div>
                <table>
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Họ và tên</th>
                            <th>Tên đăng nhập</th>
                            <th>Email</th>
                            <th>Vai trò</th>
                            <th>Hành động</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((user) => (
                            <tr key={user.id}>
                                <td>{user.id}</td>
                                <td>{user.name}</td>
                                <td>{user.username}</td>
                                <td>{user.email}</td>
                                <td>{user.role}</td>
                                <td>
                                    <Link to={`/admin/user/edit/${user.id}`} className="action-btn edit-btn">
                                        Sửa
                                    </Link>
                                    <button onClick={() => handleDelete(user.id, "users")} className="action-btn delete-btn">
                                        Xoá
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {renderPagination(userPage, userTotalPages, handleUserPageChange)}
            </div>

            {/* MOVIES SECTION */}
            <div className="section">
                <div className="flex-between">
                    <h2>Quản lý phim</h2>
                    <div>
                        <input
                            type="text"
                            className="search"
                            placeholder="Tìm phim"
                            value={searchMovie}
                            onChange={(e) => setSearchMovie(e.target.value)}
                        />
                        <Link to="/admin/movie/add" className="add-btn">
                            + Thêm phim mới
                        </Link>
                    </div>
                </div>
                <table>
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Tiêu đề</th>
                            <th>Thể loại</th>
                            <th>Thời gian</th>
                            <th>Tác giả</th>
                            <th>Hành động</th>
                        </tr>
                    </thead>
                    <tbody>
                        {movies.map((movie) => (
                            <tr key={movie.id}>
                                <td>{movie.id}</td>
                                <td>{movie.title}</td>
                                <td>{Array.isArray(movie.genres) ? movie.genres.join(", ") : "-"}</td>
                                <td>{movie.duration}</td>
                                <td>{movie.director}</td>
                                <td>
                                    <Link to={`/admin/movie/edit/${movie.id}`} className="action-btn edit-btn">
                                        Sửa
                                    </Link>
                                    <button onClick={() => handleDelete(movie.id, "movies")} className="action-btn delete-btn">
                                        Xoá
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {renderPagination(moviePage, movieTotalPages, handleMoviePageChange)}
            </div>

            <div className="genre-management-link">
                <Link to="/admin/genres" className="add-btn">
                    🎬 Quản lý thể loại phim
                </Link>
            </div>

        </div>
    );
};

export default AdminDashboard;